import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, closestCenter, useDraggable, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowLeft, Banknote, CheckCircle, ChevronDown, ChevronUp, Clock, Copy, ExternalLink, Eye, EyeOff, FileText, Link2, Link2Off, Lock, PenLine, Pin, RotateCcw, Scissors, Search, Trash2, Upload } from 'lucide-react';
import { CadernoMarkdownViewer } from './CadernoMarkdownViewer';
import { PecaRedlineView } from './PecaRedlineView';
import { abrirPecaEmNovaAba } from './abrirPecaEmNovaAba';
import { deleteObject, getDownloadURL, getStorage, ref as storageRef, uploadBytes } from 'firebase/storage';
import { computeFileSHA256 } from '../../utils/cryptoUtils';
import { processFileToPdf } from '../../utils/imageToPdfPipeline';

import { FIREBASE_CONFIG } from '../../config/Config';
import { hydrateAndBuildCanonicalClientQualification } from '../../config/ClientSchema';
import { CrmClientPickerModal } from '../../components/CrmClientPickerModal';
import { auth, db } from '../../shared/api/firebase/config';
import { insertAttachmentCitation } from '../../services/WordDocumentService';
import NumeracaoAnexosField from './NumeracaoAnexosField';
import {
  formatarNumero,
  larguraDaPeca,
  larguraNoNome,
  normalizarInicio,
  numeroDoAnexo,
  proximoNumeroDepoisDe,
  rotuloDoSlot,
  ultimaPosicaoPreenchida as ultimaPosicaoPreenchidaDe,
} from '../../utils/numeracaoAnexos';
import { sanitizeEvidenceName, formatNPU, toTitleCasePt } from '../../utils/formatters';
import { buildHonorariumFilename, generateHonorariumPDF } from '../../utils/ContractGenerator';
import { createOfficeNotification } from '../../services/NotificationService';
import { fetchClientsCache, fetchWorkspacesCache } from '../../services/FileHubCacheRepository';
import { deleteWorkspace } from '../../services/FileHubNukeRepository';
import { fetchGedLink, saveGedLink, addClientNote } from '../../services/FileHubDriveLinkRepository';
import {
  createWorkspaceDocRef, fetchWorkspaceDoc, saveWorkspaceDoc, patchWorkspaceDoc, subscribeWorkspaceDoc,
  serverTimestamp, deleteField, arrayUnion,
} from '../../services/FileHubWorkspaceSyncRepository';
import { fetchTemplate, saveTemplate } from '../../services/FileHubTemplateRepository';
import { addFinancialLedgerEntry } from '../../services/FileHubFinancialRepository';
import { checkDuplicatePetition, createPetitionIntent, commitPetition, addPetitionNote, rollbackPetition } from '../../services/FileHubPetitionRepository';
import {
  SIGNATURE_STATUS,
  createClientSignatureNotification,
  requestDocumentSignature,
  updateSignatureStatus,
} from '../../services/SignatureService';
import useOrganizerStore from '../../store/useOrganizerStore';
import useFileHub from './useFileHub';
import SlotNameEditor from './SlotNameEditor';
import SignatureRequestModal from './SignatureRequestModal';
import BillingModal from './BillingModal';
import { PARTY_ROLES_BY_GROUP } from '../../constants/partyRoles';
import { CHECKLISTS, PINNED_PIECE_TYPES } from '../../constants/autosChecklists';
import { UNIVERSAL_ROOT_DOCS, DOCUMENTOS_INSTRUCAO, CIVIL, PENAL, TRABALHO, PREVIDENCIARIO, TRIBUTARIO, ADMINISTRATIVO, PASTAS_CADERNOS_V2, resolveCadernoDocumentos, resolveCadernoDocumentosV3 } from '../../config/LegalTaxonomy';
import OrganizadorProvasModal from './OrganizadorProvasModal';
import { fetchWithWarmup, warmUpBackend } from '../../utils/backendWarmup';

const getBaseUrl = () =>
 {
  const envUrl = String(import.meta?.env?.VITE_BACKEND_URL || '').trim();
  if (envUrl) return envUrl.replace(/\/+$/, '');
  return 'https://rda-backend-thuq.onrender.com';
};

const MASTER_ACCEPTED_MIME = [
  'application/pdf',
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
  'image/gif', 'image/bmp', 'image/tiff',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MASTER_MAX_SIZE_MB = 50;
const MASTER_MAX_SIZE_BYTES = MASTER_MAX_SIZE_MB * 1024 * 1024;
const UPLOAD_RETRY_STATUSES = new Set([502, 503, 504]);
const UPLOAD_MAX_RETRIES = 3;

const JURISDICTION_OPTIONS = [
  { value: 'CIVIL', label: 'Direito Civil' },
  { value: 'PENAL', label: 'Direito Penal' },
  { value: 'TRABALHO', label: 'Direito do Trabalho' },
  { value: 'PREVIDENCIARIO', label: 'Direito Previdenciário' },
  { value: 'TRIBUTARIO', label: 'Direito Tributário' },
  { value: 'ADMINISTRATIVO', label: 'Direito Administrativo' },
  { value: 'FAMILIA', label: 'Direito de Família' },
  { value: 'EMPRESARIAL', label: 'Direito Empresarial' },
  { value: 'CONSUMIDOR', label: 'Direito do Consumidor' },
  { value: 'EDUCACIONAL', label: 'Direito Educacional' },
  { value: 'AMBIENTAL', label: 'Direito Ambiental' },
  { value: 'ANIMAL', label: 'Direito Animal' },
  { value: 'DIGITAL', label: 'Direito Digital' },
  { value: 'ELEITORAL', label: 'Direito Eleitoral' },
  { value: 'IMOBILIARIO', label: 'Direito Imobiliário' },
  { value: 'TRIBUNAIS_SUP', label: 'Tribunais Superiores (STJ / STF)' },
];

const fetchWithTimeout = (url, options = {}, timeoutMs = 15000) => {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);
  const merged = { ...(options || {}), signal: controller.signal };
  const promise = fetch(url, merged).finally(() => clearTimeout(t));
  return { controller, promise };
};

const LS_PINNED_FILES = 'rda_pinned_files';

const appId = FIREBASE_CONFIG.projectId;

const _JURISDICTION_LABEL = {
  CIVIL:          'Direito Civil',
  PENAL:          'Direito Penal',
  TRABALHO:       'Direito do Trabalho',
  PREVIDENCIARIO: 'Direito Previdenciário',
  TRIBUTARIO:     'Direito Tributário',
  TRIBUNAIS_SUP:  'Tribunais Superiores (STJ / STF)',
};
const _v2ByJurisdiction = PASTAS_CADERNOS_V2.reduce((acc, c) => {
  const key = c.jurisdiction || 'OUTROS';
  if (!acc[key]) acc[key] = [];
  acc[key].push(c.cnj_nomenclature);
  return acc;
}, {});
const PIECE_OPTGROUPS_V2 = Object.entries(_v2ByJurisdiction).map(([jur, opts]) => ({
  label: _JURISDICTION_LABEL[jur] || jur,
  options: opts,
}));

const KIT_DEFAULT = [
  'Procuração Ad Judicia',
  'Documento de Identificação (RG / CNH)',
  'Comprovante de Residência',
  'Contrato de Honorários Advocatícios',
  'Termo de Consentimento LGPD',
];

const _KIT_INICIAL_CIVEL    = ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Comprovante de Residência', 'Contrato de Honorários Advocatícios', 'Pedido de Tutela de Urgência'];
const _KIT_CONTESTACAO      = ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Contrato de Honorários Advocatícios', 'Réplica / Impugnação à Contestação', 'Petição de Especificação de Provas'];
const _KIT_RESPOSTA_ACUSACAO= ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Queixa-Crime', 'Memoriais / Alegações Finais', 'Rol de Testemunhas'];
const _KIT_HC               = ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Auto de Prisão em Flagrante', 'Pedido de Revogação de Prisão Preventiva', 'Comprovante de Residência'];
const _KIT_TRABALHISTA      = ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Comprovante de Residência', 'Contrato de Honorários Advocatícios', 'Declaração de Hipossuficiência'];
const _KIT_PREVIDENCIARIO   = ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Comprovante de Residência', 'Dossiê Médico e CNIS', 'Petição de Cumprimento de Exigência'];

const PIECE_KITS = {
  // ── Favoritos do Escritório (PINNED_PIECE_TYPES) ─────────────────────────
  'Petição Inicial (Cível)':                  _KIT_INICIAL_CIVEL,
  'Resposta à Acusação (Penal)':              _KIT_RESPOSTA_ACUSACAO,
  'Contestação (Cível)':                      _KIT_CONTESTACAO,

  // ── Cível ────────────────────────────────────────────────────────────────
  'Petição Inicial':                          _KIT_INICIAL_CIVEL,
  'Contestação':                              _KIT_CONTESTACAO,
  'Emenda à Petição Inicial':                 _KIT_INICIAL_CIVEL,
  'Pedido de Tutela de Urgência':             ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Pedido de Tutela de Urgência', 'Comprovante de Residência', 'Contrato de Honorários Advocatícios'],
  'Rol de Testemunhas':                       ['Procuração Ad Judicia', 'Rol de Testemunhas', 'Petição de Especificação de Provas'],
  'Apelação Cível':                           ['Procuração Ad Judicia', 'Apelação Cível', 'Sentença / Acórdão Impugnado', 'Preparo / Guia de Recolhimento', 'Comprovação de Tempestividade'],
  'Recurso Especial (STJ)':                   ['Procuração Ad Judicia', 'Recurso Especial (STJ)', 'Acórdão Recorrido', 'Preparo / Guia de Recolhimento', 'Comprovação de Tempestividade', 'Peças Obrigatórias'],
  'Recurso Extraordinário (STF)':             ['Procuração Ad Judicia', 'Recurso Extraordinário (STF)', 'Acórdão Recorrido', 'Preparo / Guia de Recolhimento', 'Comprovação de Tempestividade', 'Peças Obrigatórias'],
  'Agravo de Instrumento':                    ['Procuração Ad Judicia', 'Agravo de Instrumento', 'Sentença / Acórdão Impugnado', 'Certidão de Intimação', 'Contrato de Honorários Advocatícios'],
  'Requerimento de Cumprimento de Sentença':  ['Procuração Ad Judicia', 'Requerimento de Cumprimento de Sentença', 'Planilha Atualizada de Débitos', 'Certidão de Trânsito em Julgado', 'Contrato de Honorários Advocatícios'],
  'Embargos de Declaração':                   ['Procuração Ad Judicia', 'Embargos de Declaração', 'Sentença / Acórdão Impugnado', 'Contrato de Honorários Advocatícios'],

  // ── Penal ────────────────────────────────────────────────────────────────
  'Habeas Corpus':                            _KIT_HC,
  'Habeas Corpus (STJ/STF)':                  _KIT_HC,
  'Queixa-Crime':                             ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Queixa-Crime', 'Rol de Testemunhas', 'Contrato de Honorários Advocatícios'],
  'Resposta à Acusação (Defesa Prévia)':      _KIT_RESPOSTA_ACUSACAO,
  'Pedido de Liberdade Provisória':           ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Auto de Prisão em Flagrante', 'Comprovante de Residência', 'Contrato de Honorários Advocatícios'],
  'Pedido de Revogação de Prisão Preventiva': ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Auto de Prisão em Flagrante', 'Comprovante de Residência', 'Pedido de Revogação de Prisão Preventiva'],
  'Auto de Prisão em Flagrante':              ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Auto de Prisão em Flagrante', 'Pedido de Relaxamento de Prisão', 'Comprovante de Residência'],
  'Apelação Criminal':                        ['Procuração Ad Judicia', 'Apelação Criminal', 'Sentença / Acórdão Impugnado', 'Razões / Contrarrazões de Apelação', 'Comprovação de Tempestividade'],
  'Requerimento de Progressão de Regime':     ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Requerimento de Progressão de Regime', 'Laudo Médico', 'Contrato de Honorários Advocatícios'],
  'Memoriais / Alegações Finais':             ['Procuração Ad Judicia', 'Memoriais / Alegações Finais', 'Rol de Testemunhas', 'Contrato de Honorários Advocatícios'],

  // ── Trabalho ─────────────────────────────────────────────────────────────
  'Reclamação Trabalhista (Petição Inicial)': _KIT_TRABALHISTA,
  'Reclamação Trabalhista':                   _KIT_TRABALHISTA,
  'Contestação Trabalhista':                  ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Contrato de Honorários Advocatícios', 'Contestação Trabalhista', 'Manifestação sobre Defesa e Documentos'],
  'Recurso Ordinário (RO)':                   ['Procuração Ad Judicia', 'Recurso Ordinário (RO)', 'Sentença / Acórdão Impugnado', 'Depósito Recursal', 'Comprovação de Tempestividade'],

  // ── Previdenciário ───────────────────────────────────────────────────────
  'Requerimento Administrativo (Meu INSS)':   _KIT_PREVIDENCIARIO,
  'Petição Inicial Previdenciária':           ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Comprovante de Residência', 'Dossiê Médico e CNIS', 'Quesitos para Perícia Médica Judicial'],
  'Dossiê Médico e CNIS':                     _KIT_PREVIDENCIARIO,

  // ── Tributário ───────────────────────────────────────────────────────────
  'Mandado de Segurança Tributário':          ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Mandado de Segurança Tributário', 'Auto de Infração', 'Contrato de Honorários Advocatícios'],
  'Impugnação a Auto de Infração':            ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Impugnação a Auto de Infração', 'Auto de Infração', 'Contrato de Honorários Advocatícios'],
  'Embargos à Execução Fiscal':               ['Procuração Ad Judicia', 'Documento de Identificação (RG / CNH)', 'Embargos à Execução Fiscal', 'Certidão de Trânsito em Julgado', 'Contrato de Honorários Advocatícios'],
};

const getChecklistDefault = (type) => {
  // MC1 SSOT V3: V3 (100% cobertura TPU) → V2 → CHECKLISTS → PIECE_KITS → genérico
  const v3docs = resolveCadernoDocumentosV3(type);
  if (v3docs.length > 0) return v3docs.map((d) => d.label);
  const v2docs = resolveCadernoDocumentos(type);
  if (v2docs.length > 0) return v2docs.map((d) => d.label);
  const found = CHECKLISTS[type];
  if (Array.isArray(found) && found.length) return found;
  const kitFallback = PIECE_KITS[type];
  if (Array.isArray(kitFallback) && kitFallback.length) return kitFallback;
  return ['Procuração', 'Documento de Identificação', 'Comprovante de Endereço', 'Outros'];
};

const loadJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const saveJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

const arraysEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (String(a[i]) !== String(b[i])) return false;
  }
  return true;
};

const stripHtmlTags = (s) => String(s || '').replace(/<[^>]+>/g, '');

const escapeXml = (s) => {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

const wrapInOOXML = (plainText) => {
  const text = String(plainText || '');
  const ns = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

  if (!lines.length) {
    return `<w:p xmlns:w="${ns}"><w:r><w:t xml:space="preserve"></w:t></w:r></w:p>`;
  }

  return lines
    .map((line, idx) => {
      const safe = escapeXml(line);
      const lead = idx === 0 ? `<w:p xmlns:w="${ns}">` : '<w:p>';
      return `${lead}<w:r><w:t xml:space="preserve">${safe}</w:t></w:r></w:p>`;
    })
    .join('');
};

const partyRefsEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const ai = a[i] || {};
    const bi = b[i] || {};
    if (String(ai.clientId || '') !== String(bi.clientId || '')) return false;
    if (String(ai.role || '') !== String(bi.role || '')) return false;
  }
  return true;
};

const extractDriveFolderId = (raw) => {
  const t = String(raw || '').trim();
  if (!t) return '';
  const m = t.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  if (m?.[1]) return m[1];
  const m2 = t.match(/id=([a-zA-Z0-9_-]+)/);
  if (m2?.[1]) return m2[1];
  if (/^[a-zA-Z0-9_-]{10,}$/.test(t)) return t;
  return '';
};


const getExtFromName = (fileName) => {
  const m = String(fileName || '').match(/\.(\w{2,5})$/i);
  return m ? `.${m[1].toLowerCase()}` : '.pdf';
};

const ensureFileExt = (name, originalExt) => {
  const t = String(name || '').trim();
  const ext = String(originalExt || '.pdf').toLowerCase();
  if (!t) return `Arquivo${ext}`;
  if (t.toLowerCase().endsWith(ext)) return t;
  return `${t}${ext}`;
};
const ensurePdfExt = (name, originalExt) => ensureFileExt(name, originalExt);

const STOP_WORDS_PT = new Set([
  'de','do','da','dos','das','em','no','na','nos','nas',
  'por','pelo','pela','pelos','pelas','com','para','a','ao',
  'aos','e','ou','que','se','o','os','as','um','uma','uns','umas',
]);
const ACRONYMS_PT = new Set([
  'SJP','DOU','CONAB','PAD','RH','SEED','PR','SP','RJ','MG','RS','SC',
  'TJ','STJ','STF','CNJ','OAB','INSS','PJE','TJPR','TJSC','TJSP',
  'CPC','CPP','CF','CLT','ECA','CDC','CTN','PF','PJ','MS','HC',
  'ADPF','ADI','NPU','TST','TRF','TRE','MPF','AGU','IBGE',
]);

const toTitleCaseABNT = (text) => {
  let afterParen = false;
  return text.split(' ').map((word, idx) => {
    if (!word) return word;
    if (word.startsWith('(')) {
      const inner = word.slice(1);
      if (!inner) { afterParen = true; return word; }
      const up = inner.toUpperCase();
      if (ACRONYMS_PT.has(up)) return '(' + up;
      return '(' + inner.charAt(0).toUpperCase() + inner.slice(1).toLowerCase();
    }
    const upper = word.toUpperCase();
    if (ACRONYMS_PT.has(upper.replace(/[^A-Z]/g, ''))) return upper;
    if (/^\d/.test(word)) return word;
    if (/^[A-Z]+\/[A-Z]+$/.test(word) && word.split('/').every((p) => ACRONYMS_PT.has(p))) return word;
    const lower = word.toLowerCase();
    if (idx > 0 && !afterParen && STOP_WORDS_PT.has(lower)) return lower;
    afterParen = false;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');
};

const toSafeBase = (raw) => {
  // Tribunal-safe filename: no accents, no spaces, no special chars
  // Reference: PJe, e-SAJ, e-Proc all require alphanumeric + hyphen/underscore only
  const t = String(raw || '').trim();
  if (!t) return 'Arquivo';
  return t
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')   // strip diacritics
    .replace(/\.(pdf|mp3|mp4|m4a|opus|ogg|wav|jpg|jpeg|png|gif|webp|docx?|xlsx?|pptx?|zip|rar)$/i, '')  // remove trailing known ext
    .replace(/[()[\]{}'"`]/g, '')                         // remove brackets/quotes
    .replace(/[/\\|+&@#%=,:;!?]/g, '-')                  // dangerous chars → hyphen
    .replace(/\s+/g, '_')                                 // spaces → underscore
    .replace(/[^\w\-]/g, '')                              // anything else → gone
    .replace(/-{2,}/g, '-')                               // collapse multiple hyphens
    .replace(/_{2,}/g, '_')                               // collapse multiple underscores
    .replace(/^[-_]+|[-_]+$/g, '')                        // trim leading/trailing
    .toUpperCase()
    || 'Arquivo';
};

function DraggableDriveFile({ file, isAssigned, isSelected, onSelect }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: String(file.id),
    data: { type: 'driveFile', fileId: String(file.id) },
    disabled: Boolean(window?.Word),
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        'rounded-md border px-2.5 py-2 bg-white select-none cursor-pointer ' +
        (isSelected ? 'ring-2 ring-[#20243B]/30 ' : '') +
        (isAssigned ? 'border-red-400 bg-red-50' : 'border-green-300 bg-green-50')
      }
      onClick={() => {
        try {
          if (typeof onSelect === 'function') onSelect(file);
        } catch {}
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800 truncate" title={file.name}>
            {file.name}
          </div>
          <div className="text-xs text-slate-500 truncate">{file.id}</div>
        </div>

        <div className="flex items-center gap-1">
          {file.webViewLink ? (
            <a
              href={file.webViewLink}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs font-semibold text-[#20243B] hover:underline"
            >
              Abrir
            </a>
          ) : null}

          <div
            className={
              'text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded-full border ' +
              (isAssigned
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'bg-green-50 border-green-200 text-green-700')
            }
          >
            {isAssigned ? 'Vinc' : 'Livre'}
          </div>

          <div
            {...attributes}
            {...listeners}
            className="px-2 py-1 rounded-md border border-gray-300 bg-gray-50 text-xs font-semibold text-gray-700 cursor-grab active:cursor-grabbing"
          >
            :::
          </div>
        </div>
      </div>
    </div>
  );
}

function DropZone({ id, filled }) {
  const { setNodeRef, isOver } = useDroppable({ id: String(id), data: { type: 'slotDrop', dropId: String(id) } });
  const base = filled ? 'border border-red-300 bg-red-50' : 'border border-dashed border-green-300 bg-green-50/40';
  const over = isOver ? 'ring-2 ring-[#C5A059]/50 bg-[#C5A059]/10' : '';
  return (
    <div
      ref={setNodeRef}
      className={`rounded-md ${base} ${over} min-h-12 flex items-center justify-center`}
    />
  );
}

function SortableSlotCard({ id, children, highlight }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: String(id), disabled: Boolean(window?.Word) });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };
  const bgCls = highlight === 'client' ? 'rounded-md border border-red-400 bg-red-50/60 p-2' : highlight === 'procuration' ? 'rounded-md border border-[#C5A059]/50 bg-amber-50/30 p-2' : 'rounded-md border border-gray-400 bg-gray-50 p-2';

  return (
    <div ref={setNodeRef} style={style} className={bgCls}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">{children}</div>
        <div className="flex items-center gap-1">
          <div
            {...attributes}
            {...listeners}
            className="px-2 py-1 rounded-md border border-gray-300 bg-white text-xs font-semibold text-gray-700 cursor-grab active:cursor-grabbing"
          >
            :::
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FileHubArea({ activeWorkspace, activePieceId, onBack, initialFolderId, autoConnect = false, onHostBridge, pieceActions } = {}) {
  const hub = useFileHub({ activeWorkspace, activePieceId, initialFolderId, autoConnect, onBack, onHostBridge });
  // Fase C2b (02/09/2026): sub-modo do painel principal do Caderno de
  // Autos — "Documentos da Peça" (anexos, já existia) x "A Peça em Si"
  // (texto da peça em elaboração, novo). Estado local, só de UI — não
  // precisa passar pelo useFileHub compartilhado.
  const [docSubView, setDocSubView] = useState('anexos');
  // Fase C4 (02/09/2026): "Normal" (texto formatado) x "Redline" (o que
  // mudou entre as 2 últimas versões salvas, cor/riscado) — só existe
  // dentro de "A Peça em Si", não afeta o Caderno de Trabalho.
  const [pecaViewMode, setPecaViewMode] = useState('normal');
  // Fase C3 (02/09/2026): link do .md da peça em si — calculado aqui
  // direto de `activeWorkspace.pieces` (props já recebidas por este
  // componente), sem precisar expor mais um campo do useFileHub
  // compartilhado.
  const activePecaMdWebViewLink = (() => {
    const lista = Array.isArray(activeWorkspace?.pieces) ? activeWorkspace.pieces : [];
    const peca = lista.find((p) => String(p?.id || '') === String(activePieceId || ''));
    return peca?.pecaMdWebViewLink || '';
  })();
  const {
    pendingAssign, setPendingAssign,
    syncBusy, setSyncBusy,
    chaosBusy, setChaosBusy,
    hostMode, setHostMode,
    uiWidthMode, setUiWidthMode,
    workspaceHydratedKey, setWorkspaceHydratedKey,
    isRenaming, setIsRenaming,
    renameProgress,
    isEdgeProcessing, setIsEdgeProcessing,
    uploadLocks, setUploadLocks,
    workspacePartyRefs, setWorkspacePartyRefs,
    partyDetails, setPartyDetails,
    pieceType, setPieceType,
    billingModal, setBillingModal,
    nukeOpen, setNukeOpen,
    nukeText, setNukeText,
    driveFolderInput, setDriveFolderInput,
    isMirrorMode, systemInitialFolderId, zeroClickActive, isDev,
    workspaceKey, workspaceDocRef, effectivePieceKey, stateKey, currentRamoDocs,
    assignedIds, orderedDriveFiles, pendingDriveFiles, selectedDriveFile,
    completion,
    norm, resolveClientName, resolveWorkspaceLabel,
    handleAIAnalyze, handleAIToggleItem, handleAIInjectSlots,
    loadClientsCache, loadWorkspacesCache, runClientSearch, runLinkSearch,
    pushNotice, updateSlotName, moveSlot, handleAddSlot, postBridgeState,
    handleSaveMetadata,
    applyPieceTypeLabel, autoFill,
    saveChecklistAsDefault,
    handleNukeWorkspace,
    handleCiteInWord,
    handleResetSlot,
    handleRemoveSlot,
    assignToSlot,
    applyPendingAssign,
    handleDragEnd,
    uploadAndAssign,
    renameSingleFile,
    applyRenameOnDrive,
    handleMasterSyncAll,
    commitPieceType,
    listDriveFolder,
    connectToDriveFolder,
    refreshDriveFiles,
    fetchDriveFolder,
    linkDriveFileToClient,
    linkDriveFileToWorkspace,
    handlePublishPetition,
    runPortalChaos,
    syncWorkspaceNow,
    partyOmniboxRef, linkOmniboxRef, workspaceHydratedKeyRef,
    lastConfirmedOrgStateRef, metaJurisdictionRef,
    noticeTimerRef, renameTimerBySlotRef,
    driveListAbortRef, driveFolderInputRef,
    bridgePostTimerRef, autoConnectKeyRef,
    localOrganizerUpdatedAtRef,
  } = hub;

  const fb = hub.fb;
  const { notice, error, edgeInfo } = fb;

  const {
    selectedDriveFileId, setSelectedDriveFileId,
    triagedDriveFileIds, setTriagedDriveFileIds,
    driveFolderId, setDriveFolderId,
    isLoading, setIsLoading,
    driveFiles, setDriveFiles,
  } = hub.drive;

  const {
    clientsCache, setClientsCache,
    clientsCacheLoading, setClientsCacheLoading,
    workspacesCache, setWorkspacesCache,
    workspacesCacheLoading, setWorkspacesCacheLoading,
  } = hub.cache;

  const {
    linkSearch, setLinkSearch,
    linkResults, setLinkResults,
    linkOpen, setLinkOpen,
    partySearch, setPartySearch,
    partyResults, setPartyResults,
    partyOpen, setPartyOpen,
  } = hub.search;

  const {
    partyPickerOpen, setPartyPickerOpen,
    partyPickerTarget, setPartyPickerTarget,
    partyRoleDraft, setPartyRoleDraft,
  } = hub.partyPicker;

  const isWorkspaceControlled = Boolean(activeWorkspace && activeWorkspace.id);

  // No Word (taskpane), o activeWorkspace vem via bridge/Office.context — não há
  // Cadernos de Autos. A trava de "abra pelo Cadernos" só se aplica à web.
  const isWordHostEnv = typeof window !== 'undefined' && Boolean(window?.Word);
  if (!isWorkspaceControlled && !isWordHostEnv) {
    return (
      <div className="w-full">
        <div className="bg-white border border-gray-300 rounded-md p-4">
          <div className="text-sm font-semibold text-slate-900 leading-tight">Organizador de Autos</div>
          <div className="mt-2 text-sm text-slate-600">
            Abra este módulo pelo <span className="font-black">Cadernos de Autos</span> para selecionar Cliente e Tipo de Peça.
          </div>
        </div>
      </div>
    );
  }


  const {
    assignments, setAssignments,
    slotNames, setSlotNames,
    slotOrder, setSlotOrder,
    isHydrated, setHydratedState,
    resetOrganizerStore,
    clientRequested, setClientRequested,
    numeroInicialAnexos, setNumeroInicialAnexos,
    revertState, markHydrated,
  } = hub;

  // Numeração dos anexos (24/09/2026): dados do campo "começar em" da peça ativa.
  const pecaAtivaProtocoladaEm = (() => {
    const lista = Array.isArray(activeWorkspace?.pieces) ? activeWorkspace.pieces : [];
    const peca = lista.find((p) => String(p?.id || '') === String(activePieceId || ''));
    return String(peca?.protocoladoEm || '');
  })();
  // Botão "Continuar de outra peça" (D1: qualquer outra peça do MESMO caderno). Lê o documento do
  // caderno na hora do clique (estado fresco) e só CALCULA números: nunca copia anexo de uma peça
  // para outra (regra do titular, MC54).
  const carregarSugestoesContinuar = async () => {
    if (!workspaceDocRef) return [];
    const { data } = await fetchWorkspaceDoc(workspaceDocRef);
    const pecas = Array.isArray(data?.pieces) ? data.pieces : [];
    return pecas
      .filter((p) => String(p?.id || '') !== String(activePieceId || ''))
      .map((p) => {
        const org = data?.organizerStates?.[p?.id];
        const ordem = Array.isArray(org?.slotOrder) ? org.slotOrder : [];
        const inicioDela = normalizarInicio(org?.numeroInicialAnexos, ordem.length);
        return {
          id: String(p?.id || ''),
          titulo: String(p?.pieceType || 'Peça'),
          preenchidos: Object.values(org?.assignments || {}).filter((v) => v && v.id).length,
          proximo: proximoNumeroDepoisDe(inicioDela, ultimaPosicaoPreenchidaDe(ordem, org?.assignments)),
        };
      })
      .filter((s) => s.preenchidos > 0);
  };
  const larguraVigenteAnexos = larguraDaPeca(slotOrder.length, numeroInicialAnexos);
  const anexosComLarguraDiferente = slotOrder.reduce((acc, sid) => {
    const vinculado = assignments?.[sid];
    const w = larguraNoNome(vinculado?.name || '');
    return acc + (vinculado?.id && w !== null && w !== larguraVigenteAnexos ? 1 : 0);
  }, 0);

  const {
    pinnedFiles, setPinnedFiles,
  } = hub.pinned;

  const {
    checklist, setChecklist,
    editingPieceType, setEditingPieceType,
    draftPieceType, setDraftPieceType,
  } = hub.checklist;

  const {
    metaOpen, setMetaOpen,
    metaProcessMode, setMetaProcessMode,
    metaNpu, setMetaNpu,
    metaAlias, setMetaAlias,
    metaJurisdiction, setMetaJurisdiction,
    metaJurisdictionOpen, setMetaJurisdictionOpen,
    metaLiminar, setMetaLiminar,
    metaLiminarTipo, setMetaLiminarTipo,
    metaObjeto, setMetaObjeto,
    metaPartesContrarias, setMetaPartesContrarias,
    metaResumo, setMetaResumo,
    metaUrgencia, setMetaUrgencia,
    metaGratuita, setMetaGratuita,
    metaGratuitaJust, setMetaGratuitaJust,
  } = hub.meta;

  const { signatures } = hub.signatures;

  const {
    signatureModal, setSignatureModal,
    reorderModal, setReorderModal,
    petitionModal, setPetitionModal,
    petitionBusy, setPetitionBusy,
    nukeBusy, setNukeBusy,
    aiWizardOpen, setAiWizardOpen,
    aiWizardText, setAiWizardText,
    aiWizardPhase, setAiWizardPhase,
    aiWizardItems, setAiWizardItems,
  } = hub.modals;

  return (
    <div
      className={
        (hostMode === 'word' ? 'w-full flex flex-col flex-1 min-h-0 bg-slate-50' : 'w-full bg-slate-50')
      }
    >
      <div
        className={
          hostMode === 'word'
            ? 'bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden flex flex-col flex-1 min-h-0'
            : 'bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden'
        }
      >
        {/* ── SLIM BREADCRUMB removido (MC_A IDE V9) ──
             Contexto (NPU, Cliente, Sigilo, Prioridade) agora vive
             APENAS no Inspector (painel direito do CadernoCockpit),
             evitando redundância no centro. ── */}

        <div className="bg-white border-b border-gray-200 px-4 py-3">
          {/* MC_Y6 (IDE V10) — Header ESTERILIZADO (Distraction-Free).
               Seletor de Tipo de Peça → movido para Nível 3 do Explorer (CadernoCockpit).
               Botões "Salvar & Organizar" / "Abrir no Drive" / "Reset" → movidos para o
               rodapé do Explorer. Aqui fica APENAS o título da peça + ícone sutil.
               VEDAÇÃO: metadados, partes e comarca moram no Inspector (Painel Direito).
               MC41i (Fase F2, 22/08/2026): a vedação acima protegia contra
               reabrir a fragilidade de `pieceType` (estado local sincronizado
               em +10 pontos do useFileHub) — não contra qualquer botão aqui.
               `pieceActions` (prop nova, opcional) não toca em pieceType nem
               em nada do useFileHub — só chama callbacks do CadernoCockpit
               (renomear/excluir peça, aprovação, consentimento), que hoje
               vivem na árvore do Explorer. Movidos pra cá pra não duplicar o
               nome da peça (já mostrado logo abaixo) nem ficar solto na
               árvore (Nível 1, que devia ser só navegação). */}
          <div className="flex items-center gap-2 min-w-0">
            <i className="fas fa-file-lines text-[#C5A059] text-sm shrink-0" aria-hidden="true"></i>
            <h2 className="text-lg font-bold text-[#20243B] truncate">
              {String(pieceType || 'Caderno').trim()}
            </h2>
            {/* MC53 (02/09/2026, pedido do titular): sub-menu "Anexos" x
                "A Peça em Si" subiu pra cá — mesma linha do título da
                peça e dos ícones de ação. Título de vocabulário curto e
                fixo (tipos de peça), sem o risco de quebra imprevisível
                que o título do caso tem (esse sim variável/longo — por
                isso lá optamos por só cortar a linha redundante, não
                cramping de abas). */}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              <button
                type="button"
                onClick={() => setDocSubView('anexos')}
                className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded-md border ${
                  docSubView === 'anexos'
                    ? 'border-[#C5A059] bg-[#C5A059]/10 text-[#20243B]'
                    : 'border-gray-200 text-gray-400 hover:text-gray-600'
                }`}
              >
                Anexos
              </button>
              <button
                type="button"
                onClick={() => setDocSubView('peca')}
                className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded-md border ${
                  docSubView === 'peca'
                    ? 'border-[#C5A059] bg-[#C5A059]/10 text-[#20243B]'
                    : 'border-gray-200 text-gray-400 hover:text-gray-600'
                }`}
              >
                A Peça em Si
              </button>
            </div>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              {pieceActions ? (
                <div className="flex items-center gap-1">
                  {pieceActions.onRename ? (
                    <button
                      type="button"
                      onClick={pieceActions.onRename}
                      className="px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                      title="Renomear peça"
                      data-testid="btn-renomear-peca"
                    >
                      <i className="fas fa-pencil-alt text-[10px]"></i>
                    </button>
                  ) : null}
                  {pieceActions.onRemove ? (
                    <button
                      type="button"
                      onClick={pieceActions.onRemove}
                      className="px-2 py-1 rounded-md border border-red-300 bg-red-50 text-red-600 hover:bg-red-100"
                      title="Excluir peça"
                      data-testid="btn-excluir-peca"
                    >
                      <i className="fas fa-trash-alt text-[10px]"></i>
                    </button>
                  ) : null}
                  {pieceActions.onSendForApproval ? (
                    <button
                      type="button"
                      onClick={pieceActions.onSendForApproval}
                      className="px-2 py-1 rounded-md border border-[#C5A059]/60 bg-[#C5A059]/10 text-[#20243B] hover:bg-[#C5A059]/20"
                      title="Enviar para aprovação"
                      data-testid="btn-aprovacao-peca"
                    >
                      <i className="fas fa-paper-plane text-[10px]"></i>
                    </button>
                  ) : null}
                  {pieceActions.onGenerateConsent ? (
                    <button
                      type="button"
                      onClick={pieceActions.onGenerateConsent}
                      className="px-2 py-1 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      title="Gerar consentimento informado"
                      data-testid="btn-consentimento-peca"
                    >
                      <i className="fas fa-shield-alt text-[10px]"></i>
                    </button>
                  ) : null}
                  {pieceActions.onProtocolar ? (
                    <button
                      type="button"
                      onClick={pieceActions.onProtocolar}
                      className="px-2 py-1 rounded-md border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                      title="Protocolar peça (renomeia a pasta com a data real do protocolo)"
                      data-testid="btn-protocolar-peca"
                    >
                      <i className="fas fa-stamp text-[10px]"></i>
                    </button>
                  ) : null}
                </div>
              ) : null}
              {hostMode === 'word' ? (
                <div className="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 p-0.5 shrink-0">
                  {['compact', 'normal', 'wide'].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setUiWidthMode(m)}
                      className={
                        'px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ' +
                        (uiWidthMode === m ? 'bg-white text-[#20243B] shadow-sm' : 'text-gray-500 hover:text-gray-700')
                      }
                    >
                      {m[0].toUpperCase()}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {isMirrorMode && (
            <div className="mt-2 p-2 rounded-lg bg-gray-100 border border-gray-200 text-xs text-gray-600">
              Modo Word: espelho rígido (somente leitura). Conecte/atualize o Drive no Navegador e use "Sincronizar".
            </div>
          )}

          {!isMirrorMode && !systemInitialFolderId && (
            <div className="mt-3 flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Conectar Pasta do Drive</label>
                <input
                  value={driveFolderInput}
                  onChange={(e) => setDriveFolderInput(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/... ou FolderID"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A059]/40 focus:border-[#C5A059]"
                />
              </div>
              <button
                type="button"
                onClick={fetchDriveFolder}
                className="px-4 py-2 rounded-lg bg-[#20243B] text-white text-sm font-semibold hover:bg-[#20243B]/90 shadow-sm"
              >
                {isLoading || isEdgeProcessing ? 'Processando...' : 'Conectar'}
              </button>
            </div>
          )}
        </div>

        <div className={hostMode === 'word' ? 'p-2 flex-1 min-h-0 flex flex-col overflow-hidden' : 'p-2'}>
          {error ? (
            <div className="mb-3 p-2 rounded-md border border-red-200 bg-red-50 text-sm text-red-700 whitespace-pre-wrap">{error}</div>
          ) : null}
          {notice ? (
            <div className="mb-3 p-2 rounded-md border border-[#20243B]/20 bg-[#20243B]/5 text-sm text-[#20243B]">{notice}</div>
          ) : null}
          {edgeInfo ? (
            <div className="mb-3 p-2 rounded-md border border-[#C5A059]/40 bg-[#C5A059]/10 text-sm text-[#20243B]">{edgeInfo}</div>
          ) : null}

          {isMirrorMode ? (
            /* ══════════════════════════════════════════════════════════════
               MODO WORD — Vista Simplificada (Somente Leitura)
               Apenas lista de ANEXOS com botões Inserir e Citar.
               ══════════════════════════════════════════════════════════════ */
            <div className="space-y-2 flex-1 min-h-0 overflow-y-auto">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Anexos</div>
              {slotOrder.length === 0 ? (
                <div className="p-3 rounded-md border border-slate-200 bg-slate-50 text-xs text-slate-500 text-center">
                  Nenhum documento configurado. Configure os anexos no navegador.
                </div>
              ) : (
                slotOrder.map((slotId, pos) => {
                  const idx = Number(String(slotId).replace('slot-', ''));
                  const slotLabel = checklist[idx];
                  const assignedId = assignments?.[slotId]?.id || '';
                  const assignedFile = assignedId ? driveFiles.find((f) => f.id === assignedId) : null;
                  const nameValue = String(slotNames?.[slotId] ?? slotLabel ?? '');
                  const displayName = nameValue.trim() || slotLabel || 'Documento';
                  return (
                    <div key={slotId} className={`rounded-lg border p-2 transition-all ${assignedFile ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 bg-white'}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#20243B] shrink-0">
                          {formatarNumero(numeroDoAnexo(pos, numeroInicialAnexos), larguraVigenteAnexos)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-slate-800 truncate uppercase" title={displayName}>{displayName}</div>
                          <div className="text-[10px] text-slate-400 truncate">
                            {assignedFile ? `✅ ${assignedFile.name}` : '⏳ Pendente'}
                          </div>
                        </div>
                        {assignedFile ? (
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              className="px-2 py-1 rounded-md border border-[#C5A059]/60 bg-[#C5A059]/10 text-[10px] font-extrabold uppercase tracking-widest text-[#20243B] hover:bg-[#C5A059]/20 transition-colors"
                              title="Inserir citação no documento Word"
                              onClick={() => {
                                const orderedIds = (slotOrder || []).map((sid) => assignments?.[sid]?.id).filter(Boolean);
                                const citeLabel = displayName;
                                const rotuloDesteSlot = rotuloDoSlot(pos, slotOrder.length, numeroInicialAnexos);
                                handleCiteInWord(citeLabel, assignedId, assignedFile.webViewLink, orderedIds, rotuloDesteSlot);
                                pushNotice('Citação inserida.');
                              }}
                            >
                              Citar
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}

              {slotOrder.length > 0 && slotOrder.some((sid) => assignments?.[sid]?.id) && (
                <button
                  type="button"
                  className="w-full mt-2 px-3 py-2 rounded-lg border-2 border-[#C5A059] bg-[#C5A059]/10 text-xs font-extrabold uppercase tracking-widest text-[#20243B] hover:bg-[#C5A059]/20 transition-colors"
                  onClick={async () => {
                    for (let i = 0; i < slotOrder.length; i++) {
                      const sid = slotOrder[i];
                      const aId = assignments?.[sid]?.id || '';
                      if (!aId) continue;
                      const file = driveFiles.find((f) => f.id === aId);
                      if (!file) continue;
                      const label = String(slotNames?.[sid] ?? checklist[Number(String(sid).replace('slot-', ''))] ?? '');
                      const orderedIds = slotOrder.map((s) => assignments?.[s]?.id).filter(Boolean);
                      const rotuloDesteSlot = rotuloDoSlot(i, slotOrder.length, numeroInicialAnexos);
                      await handleCiteInWord(label.trim() || 'Documento', aId, file.webViewLink, orderedIds, rotuloDesteSlot);
                    }
                    pushNotice('Lista de citações inserida no documento.');
                  }}
                >
                  Gerar Lista Final no Documento
                </button>
              )}

              <div className="mt-2 p-2 rounded-md border border-slate-200 bg-slate-50 text-[10px] text-slate-400 text-center">
                Modo Word: espelho de leitura. Edite os anexos no navegador e clique "Sincronizar".
              </div>
            </div>
          ) : (
            /* ══════════ MODO NAVEGADOR — Vista Completa ══════════ */
            <>
          {/* MC_Y4 (IDE V9): bloco "Partes do Processo" REMOVIDO do centro cirúrgico.
              Gestão de partes (Autor/Réu/adversas + busca CRM) agora é responsabilidade
              EXCLUSIVA do Inspector (Painel Direito do CadernoCockpit, Acordeão "Partes").
              A Tela Central (FileHubArea) é estritamente cirúrgica:
              Header da peça + Nuvem (Drive) + Slots. Zero metadados. */}


          {pendingAssign ? (
            <div className="mb-2 p-2 rounded-md border border-[#C5A059]/40 bg-[#C5A059]/10 text-sm text-[#20243B]">
              <div className="font-semibold text-sm">⚠️ Confirmação de troca de anexo</div>
              {pendingAssign.existingSlotId ? (
                <div className="mt-1">Este PDF já está vinculado ao slot {pendingAssign.existingSlotId}. Deseja mover o vínculo?</div>
              ) : null}
              {pendingAssign.targetExistingId ? (
                <div className="mt-1">O slot {pendingAssign.targetSlotId} já possui um anexo. Deseja sobrescrever?</div>
              ) : null}
              <div className="mt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPendingAssign(null)}
                  className="px-2 py-1 rounded-md border border-[#C5A059]/40 bg-white text-sm font-semibold text-[#20243B] hover:bg-[#C5A059]/10"
                >
                  Cancelar
                </button>

                {pendingAssign.existingSlotId && pendingAssign.targetExistingId ? (
                  <button
                    type="button"
                    onClick={() => applyPendingAssign({ move: true, overwrite: true })}
                    className="px-2 py-1 rounded-md bg-[#20243B] text-white text-sm font-semibold hover:bg-[#20243B]/90"
                  >
                    Mover + Sobrescrever
                  </button>
                ) : pendingAssign.targetExistingId ? (
                  <button
                    type="button"
                    onClick={() => applyPendingAssign({ move: false, overwrite: true })}
                    className="px-2 py-1 rounded-md bg-[#20243B] text-white text-sm font-semibold hover:bg-[#20243B]/90"
                  >
                    Sobrescrever
                  </button>
                ) : pendingAssign.existingSlotId ? (
                  <button
                    type="button"
                    onClick={() => applyPendingAssign({ move: true, overwrite: false })}
                    className="px-2 py-1 rounded-md bg-[#20243B] text-white text-sm font-semibold hover:bg-[#20243B]/90"
                  >
                    Mover
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <div className={hostMode === 'word' ? 'flex flex-col gap-2 flex-1 min-h-0' : 'flex gap-2'}>
              {/* Fase C2 (02/09/2026): "Arquivos na Nuvem" ocultado no modo
                  Portal — mesma lista de arquivos já aparece no dropdown de
                  cada slot (linha ~1186, "Selecionar anexo"), duplicação
                  confirmada com o titular. Código preservado (não apagado),
                  só escondido — mesmo padrão já usado nesta função pro bloco
                  "Nenhum documento selecionado" logo abaixo. Mantido visível
                  no modo Word (hostMode==='word', usado pelo RDA Workstation/
                  bridge) — não mexido, fora do escopo desta conversa. */}
              <div
                className={
                  hostMode === 'word'
                    ? 'order-2 flex-grow'
                    : 'hidden'
                }
              >
                <div className="bg-gray-50 border border-gray-400 rounded-md p-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs font-black uppercase tracking-widest text-slate-500">Arquivos na Nuvem</div>
                    <div className="flex flex-col sm:flex-row gap-1 w-full sm:w-auto">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-200 bg-white text-sm font-semibold text-slate-600 w-full sm:w-auto">
                        {pendingDriveFiles.length} pendentes
                      </span>
                    </div>
                  </div>

                  <div className="mt-2 space-y-2">
                    {pendingDriveFiles.length === 0 ? (
                      <div className="p-2 text-sm text-slate-500">
                        {isLoading ? 'Carregando...' : 'Nenhum PDF listado. Conecte a pasta do Drive.'}
                      </div>
                    ) : (
                      pendingDriveFiles.map((f) => (
                        <DraggableDriveFile
                          key={f.id}
                          file={f}
                          isAssigned={assignedIds.has(f.id)}
                          isSelected={String(selectedDriveFileId || '') === String(f.id)}
                          onSelect={(ff) => setSelectedDriveFileId(String(ff?.id || ''))}
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div
                className={
                  hostMode === 'word'
                    ? 'order-1 flex-shrink-0'
                    : 'flex-1 min-w-0'
                }
              >
                {/* Fase C2b (02/09/2026): sub-menu "Anexos" x "A Peça em
                    Si" — pedido do titular, mais simples que aba de topo
                    nova. MC53 (02/09/2026): botões migraram pra linha do
                    título da peça (cabeçalho, ~linha 670) — título curto
                    e de vocabulário fixo ("Petição Inicial (Cível)" etc.),
                    diferente do título do caso (comprimento variável, já
                    quebra linha), então cabe sem risco junto dos ícones
                    de ação. Este bloco só decide o QUE renderizar; o
                    controle de qual aba está ativa mora no cabeçalho. */}
                {docSubView !== 'anexos' ? (
                  <div className="bg-white border border-gray-400 rounded-md p-3" style={{ height: '70vh' }}>
                    {activePecaMdWebViewLink ? (
                      <div className="flex flex-col h-full">
                        <div className="flex items-center gap-1 mb-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => setPecaViewMode('normal')}
                            className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded-md border ${
                              pecaViewMode === 'normal'
                                ? 'border-[#C5A059] bg-[#C5A059]/10 text-[#20243B]'
                                : 'border-gray-200 text-gray-400 hover:text-gray-600'
                            }`}
                          >
                            Normal
                          </button>
                          <button
                            type="button"
                            onClick={() => setPecaViewMode('redline')}
                            className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded-md border ${
                              pecaViewMode === 'redline'
                                ? 'border-[#C5A059] bg-[#C5A059]/10 text-[#20243B]'
                                : 'border-gray-200 text-gray-400 hover:text-gray-600'
                            }`}
                            title="O que mudou desde a última versão salva"
                          >
                            Redline
                          </button>
                        </div>
                        <div className="flex-1 min-h-0">
                          {pecaViewMode === 'redline' ? (
                            <PecaRedlineView
                              driveWebViewLink={activePecaMdWebViewLink}
                              onOpenNovaJanela={() => abrirPecaEmNovaAba(activeWorkspace?.id, activePieceId, 'redline')}
                            />
                          ) : (
                            // MC (15/09/2026, pedido do titular): "Nova
                            // Janela" e "Abrir no Drive" precisam ficar na
                            // MESMA linha — em vez de duplicar o link "Abrir
                            // no Drive" aqui fora, passamos o pop-out como
                            // prop pro próprio header do CadernoMarkdownViewer
                            // (mesma linha, sem duplicar SSOT). Pop-out real
                            // (janela do SO, arrastável) — pesquisado antes de
                            // construir (Fase C5, 02/09/2026): ver
                            // PecaStandaloneViewer.jsx. Nome fixo na janela
                            // faz cliques repetidos reusarem a mesma janela.
                            <CadernoMarkdownViewer
                              driveWebViewLink={activePecaMdWebViewLink}
                              onOpenNovaJanela={() => abrirPecaEmNovaAba(activeWorkspace?.id, activePieceId, 'normal')}
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center text-xs text-gray-400 py-16">
                        A peça em si ainda não foi criada para este documento.
                      </div>
                    )}
                  </div>
                ) : null}
                <div className={docSubView === 'anexos' ? 'bg-white border border-gray-400 rounded-md p-3' : 'hidden'}>
                  <div className="hidden">
                    {!selectedDriveFile ? (
                      <div className="p-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600">
                        Nenhum documento selecionado.
                      </div>
                    ) : (
                      <div className="rounded-md border border-slate-200 bg-white p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-extrabold text-slate-800 truncate" title={String(selectedDriveFile?.name || '')}>
                              {String(selectedDriveFile?.name || '')}
                            </div>
                            <div className="text-[11px] text-slate-500 truncate">{String(selectedDriveFile?.id || '')}</div>
                          </div>
                          {selectedDriveFile?.webViewLink ? (
                            <a
                              href={String(selectedDriveFile.webViewLink)}
                              target="_blank"
                              rel="noreferrer"
                              className="px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-extrabold uppercase tracking-widest text-slate-700 hover:bg-slate-50"
                            >
                              Abrir
                            </a>
                          ) : null}
                        </div>

                        <div className="mt-3">
                          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Vincular a</div>
                          <div className="relative" ref={linkOmniboxRef}>
                            <input
                              value={linkSearch}
                              onChange={(e) => {
                                setLinkSearch(e.target.value);
                                setLinkOpen(true);
                                setPartyOpen(false);
                              }}
                              onFocus={() => {
                                setLinkOpen(true);
                                setPartyOpen(false);
                                loadWorkspacesCache();
                                loadClientsCache();
                              }}
                              placeholder="Buscar workspace ou cliente para vincular..."
                              className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#C5A059]/30"
                            />
                            <div className="absolute right-2 top-2.5 text-slate-400">
                              <Search size={16} />
                            </div>

                            {linkOpen && String(linkSearch || '').trim() ? (
                              <ul className="absolute z-50 mt-1 w-full max-h-72 overflow-auto rounded-md border border-slate-200 bg-white shadow-xl">
                                {(Array.isArray(linkResults) ? linkResults : []).length === 0 ? (
                                  <li className="px-3 py-2 text-sm text-slate-500">Nenhum resultado.</li>
                                ) : (
                                  (Array.isArray(linkResults) ? linkResults : []).map((row) => {
                                    const kind = String(row?.kind || '').trim();
                                    const rid = String(row?.id || '').trim();
                                    const label = String(row?.label || '').trim();
                                    return (
                                      <li key={`${kind}::${rid}`}>
                                        <button
                                          type="button"
                                          className="w-full text-left px-3 py-2 hover:bg-slate-50"
                                          onClick={() => {
                                            setLinkOpen(false);
                                            setLinkSearch('');
                                            if (kind === 'WORKSPACE') {
                                              linkDriveFileToWorkspace({ driveFile: selectedDriveFile, workspace: row?.raw });
                                            } else {
                                              linkDriveFileToClient({ driveFile: selectedDriveFile, client: row?.raw });
                                            }
                                          }}
                                        >
                                          <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                              <div className="text-sm font-semibold text-slate-800 truncate">{label || rid}</div>
                                              <div className="text-[11px] text-slate-500 truncate">{kind === 'WORKSPACE' ? rid : 'Cliente'}</div>
                                            </div>
                                            <span
                                              className={
                                                'shrink-0 inline-flex items-center px-2 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest border ' +
                                                (kind === 'WORKSPACE'
                                                  ? 'border-blue-200 bg-blue-50 text-blue-800'
                                                  : 'border-slate-200 bg-slate-100 text-slate-700')
                                              }
                                            >
                                              {kind === 'WORKSPACE' ? 'Processo' : 'Cliente'}
                                            </span>
                                          </div>
                                        </button>
                                      </li>
                                    );
                                  })
                                )}
                              </ul>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs font-black uppercase tracking-widest text-slate-500">Anexos</div>
                    <div className="flex flex-col sm:flex-row gap-1 w-full sm:w-auto">
                      {!isMirrorMode && workspaceKey && (
                        <button
                          type="button"
                          onClick={() => setBillingModal(true)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#C5A059]/60 bg-[#C5A059]/10 text-sm font-bold text-[#20243B] hover:bg-[#C5A059]/20 w-full sm:w-auto transition"
                          title="Gerar Contrato de Honorários e registrar faturamento"
                        >
                          <Banknote size={13} /> Faturar Honorários
                        </button>
                      )}
                      {!isMirrorMode && slotOrder.length > 0 && (() => {
                        const allHidden = slotOrder.every((sid) => clientRequested?.[sid] === false);
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              setClientRequested(() => {
                                const next = {};
                                slotOrder.forEach((sid) => { next[sid] = allHidden ? true : false; });
                                return next;
                              });
                            }}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-sm font-semibold w-full sm:w-auto transition ${allHidden ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100' : 'border-red-300 bg-red-50 text-red-600 hover:bg-red-100'}`}
                            title={allHidden ? 'Tornar todos os anexos visíveis ao cliente' : 'Ocultar todos os anexos do cliente'}
                          >
                            {allHidden ? <><Eye size={13} /> Exibir Todos</> : <><EyeOff size={13} /> Ocultar Todos</>}
                          </button>
                        );
                      })()}
                      <button
                        type="button"
                        onClick={saveChecklistAsDefault}
                        className="px-2 py-1 rounded-md border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 w-full sm:w-auto"
                      >
                        Salvar como Padrão
                      </button>
                      <button
                        type="button"
                        onClick={autoFill}
                        className="px-2 py-1 rounded-md border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 w-full sm:w-auto"
                      >
                        Auto-preencher
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          resetOrganizerStore();
                          workspaceHydratedKeyRef.current = '';
                          setWorkspaceHydratedKey('');
                          localOrganizerUpdatedAtRef.current = 0;
                          clientRequestedSyncRef.current = false;
                          clientRequestedSyncKeyRef.current = '';
                          clientRequestedInitKeyRef.current = '';
                          try { Object.keys(localStorage).filter(k => k.startsWith('rda_organizer')).forEach(k => localStorage.removeItem(k)); } catch {}
                          console.log('[FORCE RESET] Store + hydration key + localTimestamp limpos. onSnapshot vai re-hidratar.');
                          fb.setNotice('Reset completo — recarregando do Firestore...');
                        }}
                        className="px-2 py-1 rounded-md border border-red-300 bg-red-50 text-sm font-semibold text-red-600 hover:bg-red-100 w-full sm:w-auto"
                      >
                        <RotateCcw size={13} className="inline mr-1" />Forçar Reset
                      </button>
                    </div>
                  </div>

                  {/* Numeração dos anexos (24/09/2026): linha própria logo abaixo do título
                      "Anexos", largura total, igual em TODA peça (a linha de cima não tem
                      espaço: 5 botões). Inserido como irmão — Art. 205.1, nunca embrulhar. */}
                  <NumeracaoAnexosField
                    valor={numeroInicialAnexos}
                    onChange={setNumeroInicialAnexos}
                    totalSlots={slotOrder.length}
                    ultimaPosicaoPreenchida={ultimaPosicaoPreenchidaDe(slotOrder, assignments)}
                    protocoladaEm={pecaAtivaProtocoladaEm}
                    arquivosParaRenomear={anexosComLarguraDiferente}
                    carregarSugestoes={carregarSugestoesContinuar}
                  />

                  <div className="relative mt-2 space-y-2">
                    {/* ITEM C (26/08/2026, corrigido 26/08/2026 — relato do usuário:
                        overlay só cobria a lista de anexos, não a tela inteira, e a
                        renomeação no Drive leva até ~30s, tempo em que o resto da tela
                        (painel "Arquivos na Nuvem", outros controles) ficava destravado.
                        Trocado de `absolute` (preso ao ancestral relative desta lista)
                        para `fixed inset-0` (viewport inteiro), mesmo padrão já usado
                        pelos outros modais bloqueantes deste arquivo — trava Drive E site
                        pelo tempo real da operação, não só uma fração dele. Art. 205.1 da
                        Constituição Anti-Retrocesso: nunca embrulhar bloco JSX grande,
                        inserir como irmão). */}
                    {(syncBusy || isRenaming) && (
                      <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-2 bg-white/90 backdrop-blur-sm">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#C5A059] border-t-transparent" />
                        <span className="text-base font-semibold text-[#20243B]">
                          {isRenaming
                            ? renameProgress?.total > 0
                              ? `Renomeando ${renameProgress.done} de ${renameProgress.total} arquivos no Drive...`
                              : 'Renomeando arquivos no Drive...'
                            : 'Salvando no Firestore...'}
                        </span>
                        <span className="text-[12px] text-slate-500">Não feche nem edite até terminar.</span>
                      </div>
                    )}
                    <SortableContext items={slotOrder} strategy={verticalListSortingStrategy}>
                      {slotOrder.map((slotId, pos) => {
                        const idx = Number(String(slotId).replace('slot-', ''));
                        const slotLabel = checklist[idx];
                        const assignedId = assignments?.[slotId]?.id || '';
                        const assignedFile = assignedId
                          ? driveFiles.find((f) => f.id === assignedId)
                            || { id: assignedId, name: assignments?.[slotId]?.name || '', webViewLink: assignments?.[slotId]?.url || '' }
                          : null;
                        const nameValue = String(slotNames?.[slotId] ?? slotLabel ?? '');
                        const base = nameValue.trim() ? nameValue.trim() : slotLabel;
                        const origExt = getExtFromName(assignments?.[slotId]?.name || '');
                        // Rótulo único do slot (função única): "ANEXO 07" / "ANEXO 007" conforme a largura
                        // vigente da peça e o "começar em". Mesmo texto na tela, no Drive, no ZIP e no Word.
                        const rotuloSlot = rotuloDoSlot(pos, slotOrder.length, numeroInicialAnexos);
                        const desired = ensurePdfExt(toSafeBase(`${rotuloSlot} ${base}`), origExt);
                        const dropId = `drop-${slotId}`;
                        const inputId = `file-${slotId}`;
                        const slotHighlight = String(slotId).startsWith('procuracao-') ? 'procuration' : String(slotId).startsWith('slot-client-extra-') ? 'client' : undefined;

                        return (
                          <SortableSlotCard key={slotId} id={slotId} highlight={slotHighlight}>
                            {/* Linha 1: ANEXO — nome via Taxonomia CNJ + Escape Hatch personalizado (Passo 31) */}
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="text-xs font-extrabold uppercase tracking-widest text-[#20243B] shrink-0">{rotuloSlot}</span>
                              <span className="text-slate-300 flex-shrink-0 text-[9px] font-normal">/</span>
                              <SlotNameEditor
                                nameValue={nameValue}
                                isMirrorMode={isMirrorMode}
                                placeholder={slotLabel || 'Nome do documento...'}
                                ramoContext={currentRamoDocs}
                                // FIX GATILHOS AUTOMÁTICOS (26/08/2026): onChangeName/onBlurName
                                // renomeavam no Drive (0,9s digitando / imediato ao sair do
                                // campo) e onBlurName ainda gravava direto no Firestore — os
                                // dois por fora do botão "Salvar & Organizar", correndo em
                                // paralelo com o autosave geral e podendo gravar posição
                                // errada se o slotOrder estivesse temporariamente inconsistente.
                                // Agora: digitar/sair do campo só atualiza o estado local (tela);
                                // nada vai pro Drive nem pro Firestore até "Salvar & Organizar"
                                // (que já lê slotNames do estado local e faz as duas coisas de
                                // forma controlada — ver applyRenameOnDrive/syncWorkspaceNow).
                                onChangeName={(nextValue) => {
                                  if (isMirrorMode) return;
                                  updateSlotName(slotId, nextValue);
                                }}
                                onBlurName={(currentValue) => {
                                  const trimmed = String(currentValue || '').trim();
                                  if (trimmed) updateSlotName(slotId, trimmed);
                                }}
                              />
                            </div>

                            {/* Linha 2: dropdown + upload — largura total */}
                            <div className="mt-1 flex items-center gap-1">
                              <select
                                value={assignedId || ''}
                                onChange={(ev) => {
                                  ev.preventDefault(); ev.stopPropagation();
                                  const v = String(ev.target.value || '').trim();
                                  if (!v) return;
                                  assignToSlot(slotId, v);
                                }}
                                className={`text-[11px] font-semibold border rounded px-1 py-0.5 flex-1 min-w-0 truncate ${assignedId ? 'text-amber-700 border-amber-300 bg-amber-50' : 'text-slate-700 border-gray-200 bg-white hover:bg-gray-50'}`}
                              >
                                <option value="">Selecionar anexo</option>
                                {assignedId && !orderedDriveFiles.some((f) => String(f.id) === assignedId) && (
                                  <option value={assignedId}>{String(assignments?.[slotId]?.name || 'Arquivo vinculado').replace(/^\d{10,}_/, '')}</option>
                                )}
                                {orderedDriveFiles.map((f) => {
                                  const rawName = String(f.name || '');
                                  const cleanName = rawName.replace(/^\d{10,}_/, '');
                                  const isUsed = assignedIds.has(String(f.id));
                                  return (
                                  <option key={String(f.id)} value={String(f.id)} style={{ backgroundColor: isUsed ? '#fef2f2' : '#f0fdf4', color: isUsed ? '#b91c1c' : '#15803d' }}>
                                    {isUsed ? '● ' : '○ '}{cleanName}
                                  </option>
                                  );
                                })}
                              </select>
                              <label htmlFor={inputId} className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 bg-white text-slate-500 hover:bg-gray-50 hover:text-slate-700 transition-colors shrink-0 cursor-pointer" title="Upload de arquivo do computador"><Upload className="w-3.5 h-3.5" /></label>
                              <input
                                id={inputId}
                                type="file"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.tiff,.bmp,.webp,.heic,.heif"
                                className="sr-only"
                                onChange={async (ev) => {
                                  let rawFile = ev.target.files?.[0];
                                  ev.target.value = '';
                                  if (!rawFile) return;
                                  console.log('[UPLOAD] Arquivo selecionado:', rawFile.name, rawFile.type, rawFile.size, 'driveFolderId:', driveFolderId);

                                  // ── Validação MIME + tamanho ──
                                  const mimeOk = MASTER_ACCEPTED_MIME.includes(rawFile.type) || /\.(heic|heif)$/i.test(rawFile.name);
                                  if (!mimeOk) { pushNotice(`Formato não aceito (${rawFile.type || 'desconhecido'}). Use PDF, imagem ou DOCX.`); return; }
                                  if (rawFile.size > MASTER_MAX_SIZE_BYTES) { pushNotice(`Arquivo muito grande (${(rawFile.size / 1024 / 1024).toFixed(1)} MB). Máximo: ${MASTER_MAX_SIZE_MB} MB.`); return; }
                                  const effectiveFolderId = driveFolderId || systemInitialFolderId;
                                  if (!effectiveFolderId) { pushNotice('Conecte uma pasta do Drive antes de fazer upload.'); console.warn('[UPLOAD] Bloqueado: driveFolderId vazio'); return; }
                                  const token = await getToken().catch(() => null);
                                  if (!token) { pushNotice('Usuário não autenticado.'); console.warn('[UPLOAD] Bloqueado: sem token'); return; }

                                  // ── Pipeline PDF: imagens → PDF A4 ──
                                  const isImageFile = rawFile.type?.startsWith('image/') || /\.(heic|heif|jpe?g|png|webp|gif|bmp|tiff?)$/i.test(rawFile.name);
                                  if (isImageFile && typeof processFileToPdf === 'function') {
                                    try {
                                      rawFile = await processFileToPdf(rawFile);
                                      console.log('[UPLOAD] Pipeline PDF concluído:', rawFile.name);
                                    } catch (pdfErr) {
                                      console.warn('[UPLOAD] Pipeline PDF falhou, usando original:', pdfErr);
                                    }
                                  }

                                  const baseUrl = getBaseUrl();
                                  const labelBase = String(slotNames?.[slotId] || slotLabel || '').trim();
                                  const uploadExt = getExtFromName(rawFile.name || '');
                                  const safeName = ensurePdfExt(toSafeBase(`${rotuloSlot} ${labelBase || rawFile.name}`), uploadExt);
                                  pushNotice(`Enviando "${rawFile.name}"...`);
                                  console.log('[UPLOAD] Iniciando upload:', safeName, '→', `${baseUrl}/drive/upload-file`, 'parentId:', driveFolderId);

                                  // ── SAGA Passo 1: Intent + Lock (Pré-Reserva) ──
                                  if (workspaceDocRef) {
                                    try { await patchWorkspaceDoc(workspaceDocRef, { [`_uploadLocks.${slotId}`]: Date.now(), [`_uploadIntents.${slotId}`]: { name: safeName, startedAt: serverTimestamp() } }); } catch {}
                                  }

                                  try {
                                    const mime = rawFile.type || 'application/octet-stream';
                                    let driveResult = null;

                                    // ── Caminho 1: Direct-to-Cloud (resumable, bypassa Render) ──
                                    const CHUNK_SIZE = 5 * 1024 * 1024;
                                    if (rawFile.size > 0) {
                                      try {
                                        const sessionRes = await fetchWithWarmup(`${baseUrl}/drive/generate-resumable-session-uri`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                          body: JSON.stringify({ name: safeName, mimeType: mime, parentId: effectiveFolderId, fileSize: rawFile.size }),
                                        });
                                        if (sessionRes.ok) {
                                          const sessionData = await sessionRes.json().catch(() => ({}));
                                          const sessionUri = String(sessionData?.sessionUri || '').trim();
                                          if (sessionUri) {
                                            const totalBytes = rawFile.size;
                                            let offset = 0;
                                            const MAX_CHUNK_RETRIES = 3;
                                            while (offset < totalBytes) {
                                              const end = Math.min(offset + CHUNK_SIZE, totalBytes);
                                              const chunk = rawFile.slice(offset, end);
                                              const rangeHeader = `bytes ${offset}-${end - 1}/${totalBytes}`;
                                              let chunkSuccess = false;
                                              for (let attempt = 1; attempt <= MAX_CHUNK_RETRIES; attempt++) {
                                                try {
                                                  const putRes = await fetch(sessionUri, {
                                                    method: 'PUT',
                                                    headers: { 'Content-Type': mime, 'Content-Range': rangeHeader },
                                                    body: chunk,
                                                  });
                                                  if (putRes.status === 200 || putRes.status === 201) {
                                                    driveResult = await putRes.json().catch(() => ({}));
                                                    console.info('[UPLOAD] Direct-to-Cloud concluído ✓', driveResult?.id);
                                                    offset = totalBytes;
                                                    chunkSuccess = true;
                                                    break;
                                                  }
                                                  if (putRes.status === 308) {
                                                    const range = putRes.headers.get('range') || '';
                                                    const match = range.match(/bytes=0-(\d+)/);
                                                    offset = match ? parseInt(match[1], 10) + 1 : end;
                                                    pushNotice(`Enviando... ${Math.round((offset / totalBytes) * 100)}%`);
                                                    chunkSuccess = true;
                                                    break;
                                                  }
                                                  if (attempt === MAX_CHUNK_RETRIES) break;
                                                  await new Promise((r) => setTimeout(r, 1000 * attempt));
                                                } catch (chunkErr) {
                                                  console.warn(`[UPLOAD] Chunk falhou (${attempt}/${MAX_CHUNK_RETRIES}):`, chunkErr?.message);
                                                  if (attempt === MAX_CHUNK_RETRIES) break;
                                                  try {
                                                    const probeRes = await fetch(sessionUri, { method: 'PUT', headers: { 'Content-Range': `bytes */${totalBytes}` } });
                                                    if (probeRes.status === 308) {
                                                      const range = probeRes.headers.get('range') || '';
                                                      const match = range.match(/bytes=0-(\d+)/);
                                                      if (match) offset = parseInt(match[1], 10) + 1;
                                                    }
                                                  } catch { /* probe falhou */ }
                                                  await new Promise((r) => setTimeout(r, 2000 * attempt));
                                                }
                                              }
                                              if (!chunkSuccess && offset < totalBytes) {
                                                console.warn('[UPLOAD] Chunking falhou, caindo no fallback.');
                                                break;
                                              }
                                            }
                                          }
                                        }
                                      } catch (sessionErr) {
                                        console.warn('[UPLOAD] Session URI indisponível, usando fallback:', sessionErr?.message);
                                      }
                                    }

                                    // ── Caminho 2: Fallback via Render ──
                                    if (!driveResult?.id) {
                                      const form = new FormData();
                                      form.append('file', rawFile);
                                      form.append('parentId', effectiveFolderId);
                                      form.append('fileName', safeName);
                                      form.append('makePublic', 'false');
                                      await warmUpBackend();
                                      let resp;
                                      for (let attempt = 1; attempt <= UPLOAD_MAX_RETRIES; attempt++) {
                                        try {
                                          resp = await fetch(`${baseUrl}/drive/upload-file`, {
                                            method: 'POST',
                                            headers: { Authorization: `Bearer ${token}` },
                                            body: form,
                                          });
                                          console.log(`[UPLOAD] Attempt ${attempt} status:`, resp.status);
                                        } catch (netErr) {
                                          console.error(`[UPLOAD] Attempt ${attempt} network error:`, netErr);
                                          if (attempt === UPLOAD_MAX_RETRIES) throw new Error('Falha de conexão com o servidor. Tente novamente.');
                                          pushNotice(`Tentativa ${attempt}/${UPLOAD_MAX_RETRIES} falhou. Retentando...`);
                                          await new Promise((r) => setTimeout(r, 2000 * attempt));
                                          continue;
                                        }
                                        if (UPLOAD_RETRY_STATUSES.has(resp.status)) {
                                          if (attempt === UPLOAD_MAX_RETRIES) throw new Error(`Servidor indisponível (${resp.status}). Tente novamente em instantes.`);
                                          pushNotice(`Servidor em standby (${resp.status}). Tentativa ${attempt}/${UPLOAD_MAX_RETRIES}...`);
                                          await new Promise((r) => setTimeout(r, 2000 * attempt));
                                          continue;
                                        }
                                        break;
                                      }
                                      const result = await resp.json().catch(() => ({}));
                                      console.log('[UPLOAD] Resposta backend:', resp.status, result);
                                      if (!resp.ok) throw new Error(String(result?.detail || result?.error || `Falha no upload (${resp.status}).`));
                                      driveResult = result;
                                    }

                                    const fileId = String(driveResult?.fileId || driveResult?.id || '').trim();
                                    const webViewLink = String(driveResult?.webViewLink || '').trim();
                                    const fileUrl = webViewLink || (fileId ? `gdrive://${fileId}` : '');

                                    if (fileId) {
                                      setDriveFiles((prev) => [...(prev || []), { id: fileId, name: safeName, webViewLink }]);
                                      assignToSlot(slotId, fileId);

                                      if (workspaceDocRef) {
                                        try {
                                          // ── SAGA Passo 3: Commit — grava assignment + limpa Intent ──
                                          await patchWorkspaceDoc(workspaceDocRef, {
                                            [`organizerState.assignments.${slotId}.id`]: fileId,
                                            [`organizerState.assignments.${slotId}.name`]: safeName,
                                            [`organizerState.assignments.${slotId}.url`]: fileUrl,
                                            [`organizerState.assignments.${slotId}.multiFiles`]: arrayUnion({ id: fileId, name: safeName, url: fileUrl }),
                                            [`organizerStates.${effectivePieceKey}.assignments.${slotId}.id`]: fileId,
                                            [`organizerStates.${effectivePieceKey}.assignments.${slotId}.name`]: safeName,
                                            [`organizerStates.${effectivePieceKey}.assignments.${slotId}.url`]: fileUrl,
                                            [`organizerStates.${effectivePieceKey}.assignments.${slotId}.multiFiles`]: arrayUnion({ id: fileId, name: safeName, url: fileUrl }),
                                            [`_uploadIntents.${slotId}`]: deleteField(),
                                            organizerStateUpdatedAtMs: Date.now(),
                                                                                      });
                                          localOrganizerUpdatedAtRef.current = Date.now();
                                          console.log('[UPLOAD] Firestore assignment gravado:', slotId, fileId);
                                        } catch (fsErr) {
                                          console.error('[UPLOAD] Falha Firestore assignment:', fsErr);
                                        }
                                      }

                                      try { await refreshDriveFiles(); } catch {}
                                      pushNotice(`Upload concluído: ${safeName}`);
                                    } else {
                                      console.warn('[UPLOAD] Backend não retornou fileId:', result);
                                      pushNotice('Upload enviado mas sem ID retornado.');
                                    }
                                  } catch (err) {
                                    console.error('[UPLOAD] Erro:', err);
                                    pushNotice(`Erro no upload: ${String(err?.message || err)}`);
                                  } finally {
                                    // ── SAGA Passo 4: Rollback/Cleanup — limpa Intent + Lock ──
                                    if (workspaceDocRef) {
                                      try { await patchWorkspaceDoc(workspaceDocRef, { [`_uploadLocks.${slotId}`]: 0, [`_uploadIntents.${slotId}`]: deleteField() }); } catch {}
                                    }
                                  }
                                }}
                              />
                            </div>

                            {/* Linha 3: botões de ação */}
                            <div className="mt-1 flex items-center gap-1 flex-wrap">
                              <button type="button" onClick={() => moveSlot(slotId, 'up')} disabled={pos === 0} className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 bg-white text-slate-500 hover:bg-gray-50 hover:text-slate-700 transition-colors shrink-0 disabled:opacity-30" title="Mover para cima"><ChevronUp className="w-3.5 h-3.5" /></button>
                              <button type="button" onClick={() => moveSlot(slotId, 'down')} disabled={pos === slotOrder.length - 1} className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 bg-white text-slate-500 hover:bg-gray-50 hover:text-slate-700 transition-colors shrink-0 disabled:opacity-30" title="Mover para baixo"><ChevronDown className="w-3.5 h-3.5" /></button>
                              <span className="w-1 shrink-0" />
                              <button type="button" title={assignedFile ? 'Desvincular arquivo' : 'Desvincular (sem arquivo)'} disabled={!assignedFile} onClick={() => { if (assignedFile) assignToSlot(slotId, null); }} className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border transition-colors shrink-0 ${assignedFile ? 'border-red-400 bg-red-50 text-red-500 hover:bg-red-100 cursor-pointer' : 'border-gray-300 bg-gray-50 text-gray-400 opacity-40 cursor-default'}`}><Link2Off className="w-3.5 h-3.5" /></button>
                              <button type="button" title={clientRequested?.[slotId] !== false ? 'Visível ao cliente' : 'Oculto do cliente'} onClick={() => setClientRequested((prev) => { const next = { ...(prev || {}) }; next[slotId] = next[slotId] === false ? true : false; return next; })} className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border transition-colors shrink-0 ${clientRequested?.[slotId] !== false ? 'border-green-400 bg-green-50 text-green-600 hover:bg-green-100' : 'border-red-300 bg-red-50 text-red-500 hover:bg-red-100'}`}>{clientRequested?.[slotId] !== false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}</button>
                              <span title={assignments?.[slotId]?.id ? 'Recebido' : 'Pendente'} className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border transition-colors shrink-0 cursor-default ${clientRequested?.[slotId] === false ? 'border-gray-300 bg-gray-50 text-gray-400 opacity-50' : assignments?.[slotId]?.id ? 'border-emerald-400 bg-emerald-50 text-emerald-600' : 'border-amber-300 bg-amber-50 text-amber-600'}`}>{assignments?.[slotId]?.id ? <CheckCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}</span>
                              {(() => {
                                const lockTs = Number(uploadLocks?.[slotId] || 0);
                                const isClientUploading = lockTs > 0 && (Date.now() - lockTs) < 300000;
                                if (isClientUploading) return (
                                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-blue-400 bg-blue-50 text-blue-500 shrink-0 cursor-not-allowed animate-pulse" title="Cliente enviando arquivos — aguarde"><RotateCcw className="w-3.5 h-3.5" /></span>
                                );
                                if (assignedId) return (
                                  <button type="button" onClick={() => handleResetSlot(slotId)} className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-orange-300 bg-orange-50 text-orange-500 hover:border-orange-500 hover:bg-orange-100 hover:text-orange-700 transition-colors shrink-0" title="Apagar do Drive e reabrir slot para o cliente"><RotateCcw className="w-3.5 h-3.5" /></button>
                                );
                                return (
                                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-300 bg-gray-50 text-gray-400 shrink-0 cursor-default" title="Reset (sem arquivo)"><RotateCcw className="w-3.5 h-3.5" /></span>
                                );
                              })()}
                              {String(slotId).startsWith('slot-client-extra-') && assignedFile && (
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-purple-400 bg-purple-50 text-purple-600 shrink-0 cursor-default" title="Documento extra enviado pelo cliente (pode conter múltiplos arquivos)"><Copy className="w-3.5 h-3.5" /></span>
                              )}
                              <button type="button" onClick={() => handleRemoveSlot(slotId)} className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-red-200 bg-red-50 text-red-400 hover:border-red-400 hover:bg-red-100 hover:text-red-600 transition-colors shrink-0" title="Remover slot"><Trash2 className="w-3.5 h-3.5" /></button>
                              <span className="w-1 shrink-0" />
                              {(() => {
                                const rawUrl = assignedFile?.webViewLink || '';
                                const openUrl = rawUrl.startsWith('gdrive://') ? `https://drive.google.com/file/d/${rawUrl.replace('gdrive://', '')}/view` : rawUrl;
                                return openUrl ? (
                                  <a href={openUrl} target="_blank" rel="noreferrer" onClick={(ev) => ev.stopPropagation()} className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-blue-300 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors shrink-0" title="Abrir no Drive"><ExternalLink className="w-3.5 h-3.5" /></a>
                                ) : (
                                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-300 bg-gray-50 text-gray-400 shrink-0 cursor-default opacity-40" title="Abrir (sem arquivo)"><ExternalLink className="w-3.5 h-3.5" /></span>
                                );
                              })()}
                              {assignedFile && !isMirrorMode && signatures?.[slotId]?.status !== SIGNATURE_STATUS.SIGNED ? (
                                <button type="button" title="Assinar" onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setSignatureModal({ slotId, slotLabel, fileName: assignedFile.name }); }} className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-[#C5A059]/60 bg-[#C5A059]/10 text-[#20243B] hover:bg-[#C5A059]/20 transition-colors shrink-0"><PenLine className="w-3.5 h-3.5" /></button>
                              ) : (
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-300 bg-gray-50 text-gray-400 shrink-0 cursor-default opacity-40" title="Assinar (sem arquivo)"><PenLine className="w-3.5 h-3.5" /></span>
                              )}
                              {assignedFile && !isMirrorMode && String(assignedFile.name || '').toLowerCase().endsWith('.pdf') ? (
                                <button type="button" title="Organizador de Provas — Reordenar páginas" onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setReorderModal({ fileId: assignedId, fileName: assignedFile.name }); }} className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-violet-300 bg-violet-50 text-violet-600 hover:bg-violet-100 hover:border-violet-400 transition-colors shrink-0"><Scissors className="w-3.5 h-3.5" /></button>
                              ) : (
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-300 bg-gray-50 text-gray-400 shrink-0 cursor-default opacity-40" title="Organizador de Provas (apenas PDF)"><Scissors className="w-3.5 h-3.5" /></span>
                              )}
                              {assignedFile && !isMirrorMode ? (
                                <button type="button" title="Publicar como Petição para o Cliente" onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); const primaryParty = (Array.isArray(workspacePartyRefs) ? workspacePartyRefs : []).find((p) => String(p?.clientId || '').trim()); setPetitionModal({ slotId, slotLabel: String(nameValue || slotLabel || '').trim(), fileName: assignedFile.name, fileId: assignedId, clientId: String(primaryParty?.clientId || '').trim(), clientName: String(primaryParty?.name || '').trim(), cadernoId: workspaceKey || '', cadernoTitle: String(pieceType || activeWorkspace?.title || '').trim(), parteContraria: (Array.isArray(metaPartesContrarias) ? metaPartesContrarias : []).map((p) => String(p?.name || '').trim()).filter(Boolean).join(', ') }); }} className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-500 transition-colors shrink-0"><FileText className="w-3.5 h-3.5" /></button>
                              ) : (
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-300 bg-gray-50 text-gray-400 shrink-0 cursor-default opacity-40" title="Publicar Petição (sem arquivo)"><FileText className="w-3.5 h-3.5" /></span>
                              )}
                              {assignedFile ? (
                                <div onMouseDown={(ev) => { ev.preventDefault(); ev.stopPropagation(); }} onPointerDown={(ev) => { ev.preventDefault(); ev.stopPropagation(); }} onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); const now = Date.now(); if (now - (lastCiteAtRef.current || 0) < 800) return; lastCiteAtRef.current = now; const orderedIds = (slotOrder || []).map((sid) => assignments?.[sid]?.id).filter(Boolean); const citeLabel = String(nameValue || '').trim() || String(slotLabel || '').trim(); handleCiteInWord(citeLabel, assignedId, assignedFile.webViewLink, orderedIds); pushNotice('Citação inserida.'); }} className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 bg-white text-slate-500 hover:bg-gray-50 hover:text-slate-700 transition-colors shrink-0 cursor-pointer" title="Citar no Word"><Search className="w-3.5 h-3.5" /></div>
                              ) : (
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-300 bg-gray-50 text-gray-400 shrink-0 cursor-default opacity-40" title="Citar (sem arquivo)"><Search className="w-3.5 h-3.5" /></span>
                              )}
                              {assignedFile ? (
                                <div onMouseDown={(ev) => { ev.preventDefault(); ev.stopPropagation(); }} onPointerDown={(ev) => { ev.preventDefault(); ev.stopPropagation(); }} onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); const label = String(slotLabel || '').trim(); if (!label || isMirrorMode) return; setPinnedFiles((prev) => { const next = { ...(prev || {}) }; if (String(next[label] || '') === String(assignedId || '')) { delete next[label]; } else { next[label] = String(assignedId || ''); } return next; }); }} className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border transition-colors shrink-0 cursor-pointer ${String(pinnedFiles?.[String(slotLabel || '').trim()] || '') === String(assignedId || '') ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'}`} title="Fixar como padrão"><Pin className="w-3.5 h-3.5" /></div>
                              ) : (
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-300 bg-gray-50 text-gray-400 shrink-0 cursor-default opacity-40" title="Fixar (sem arquivo)"><Pin className="w-3.5 h-3.5" /></span>
                              )}
                              {assignedFile && (() => {
                                const sigStatus = signatures?.[slotId]?.status;
                                if (sigStatus === SIGNATURE_STATUS.PENDING_GOVBR) return <span className="text-[8px] font-black uppercase px-1 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 shrink-0">Gov.br</span>;
                                if (sigStatus === SIGNATURE_STATUS.PENDING_CLICKSIGN) return <span className="text-[8px] font-black uppercase px-1 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 shrink-0">Clicksign</span>;
                                if (sigStatus === SIGNATURE_STATUS.REVIEW_PENDING) return <button type="button" onClick={() => updateSignatureStatus(workspaceKey, slotId, SIGNATURE_STATUS.SIGNED).then(() => createOfficeNotification('✅ Assinatura Confirmada', `Documento "${assignedFile.name}" marcado como assinado.`, 'SYSTEM', null))} className="text-[8px] font-black uppercase px-1 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-300 hover:bg-orange-200 shrink-0 cursor-pointer">Confirmar</button>;
                                if (sigStatus === SIGNATURE_STATUS.SIGNED) return <span className="text-[8px] font-black uppercase px-1 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 shrink-0">Assinado</span>;
                                return null;
                              })()}
                            </div>

                          </SortableSlotCard>
                        );
                      })}
                    </SortableContext>

                    {!isMirrorMode && (
                      <div className="mt-3 flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={handleAddSlot}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-dashed border-[#C5A059]/50 text-[#20243B] text-xs font-bold hover:border-[#C5A059] hover:bg-[#C5A059]/5 transition-colors"
                        >
                          <span className="text-base leading-none">+</span>
                          Adicionar Documento
                        </button>
                        <button
                          type="button"
                          onClick={() => { setAiWizardText(''); setAiWizardPhase('input'); setAiWizardItems([]); setAiWizardOpen(true); }}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-dashed border-purple-300 text-purple-700 text-xs font-bold hover:border-purple-500 hover:bg-purple-50 transition-colors"
                          title="Cole um texto gerado por IA (NotebookLM, Gemini) e o sistema cria os slots automaticamente"
                        >
                          <span className="text-base leading-none">🪄</span>
                          Importar da IA
                        </button>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            </div>
          </DndContext>
            </>
          )}
        </div>
      </div>
      {billingModal && (
        <BillingModal
          clientName={String(workspacePartyRefs?.[0]?.name || workspacePartyRefs?.[0]?.clientId || 'Cliente')}
          clientId={String(workspacePartyRefs?.[0]?.clientId || '')}
          processLabel={resolveWorkspaceLabel(activeWorkspace)}
          npu={String(activeWorkspace?.npu || '')}
          driveFolderId={extractDriveFolderId(driveFolderInput)}
          onConfirm={async ({ amount, dueDate, description }) => {
            setBillingModal(false);
            const clientName = String(workspacePartyRefs?.[0]?.name || workspacePartyRefs?.[0]?.clientId || 'Cliente');
            const clientId   = String(workspacePartyRefs?.[0]?.clientId || '');
            const label      = resolveWorkspaceLabel(activeWorkspace);
            const npu        = String(activeWorkspace?.npu || '');
            const folderId   = extractDriveFolderId(driveFolderInput);
            if (!folderId) { pushNotice('Configure a pasta do Drive antes de faturar.'); return; }
            try {
              pushNotice('Gerando PDF de honorários...');
              const blob     = await generateHonorariumPDF({ clientName, clientId, processLabel: label, npu, amount, dueDate, description });
              const fileName = buildHonorariumFilename(clientName);
              const pdfFile  = new File([blob], fileName, { type: 'application/pdf' });
              const token    = await getToken(true).catch(() => null);
              const form     = new FormData();
              form.append('file', pdfFile, fileName);
              form.append('parentId', folderId);
              form.append('fileName', fileName);
              form.append('makePublic', 'false');
              const resp   = await fetchWithWarmup(`${getBaseUrl()}/drive/upload-file`, { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: form });
              const result = await resp.json().catch(() => ({}));
              if (!resp.ok) throw new Error(String(result?.detail || result?.error || 'Falha no upload.'));
              const fileId  = String(result?.fileId || result?.id || '').trim();
              const fileUrl = String(result?.webViewLink || result?.webContentLink || '').trim();
              if (!fileId) throw new Error('Drive não retornou fileId.');
              const newSlotId = `slot-extra-billing-${Date.now()}`;
              setSlotOrder((prev) => [...(Array.isArray(prev) ? prev : []), newSlotId]);
              setSlotNames((prev) => ({ ...(prev || {}), [newSlotId]: 'Contrato de Honorários' }));
              setAssignments((prev) => ({ ...(prev || {}), [newSlotId]: { id: fileId, name: fileName, url: fileUrl } }));
              const amountSafe = Math.round(Number(amount) * 100) / 100;
              const amountCents = Math.round(Number(amount) * 100);
              await addFinancialLedgerEntry(appId, {
                type:            'INCOME',
                status:          'PENDING',
                amount:          amountSafe,
                amountCents,
                description:     String(description || 'Honorários advocatícios').slice(0, 200),
                clientId:        clientId || null,
                competenceDate:  new Date().toISOString().slice(0, 10),
                paymentDate:     null,
                dueDate:         String(dueDate || ''),
                linkedWorkspace: workspaceKey || null,
                processLabel:    label || null,
                npu:             npu   || null,
                pdfUrl:          fileUrl || null,
                driveFileId:     fileId,
              });
              await requestDocumentSignature(workspaceKey, newSlotId, 'CLICKSIGN', null);
              createClientSignatureNotification(workspaceKey, 'Contrato de Honorários', 'CLICKSIGN');
              createOfficeNotification('💰 Faturamento Registrado', `Honorários de ${toTitleCasePt(clientName)} — R$ ${amountSafe.toFixed(2).replace('.', ',')} registrado no ledger.`, 'BILLING', null);
              pushNotice('Honorários faturados e assinatura solicitada!');
            } catch (e) {
              pushNotice(`Erro no faturamento: ${String(e?.message || e).slice(0, 80)}`);
            }
          }}
          onClose={() => setBillingModal(false)}
        />
      )}
      {signatureModal && (
        <SignatureRequestModal
          fileName={signatureModal.fileName}
          slotLabel={signatureModal.slotLabel}
          onConfirm={async (method, link) => {
            await requestDocumentSignature(workspaceKey, signatureModal.slotId, method, link);
            const label = String(signatureModal.slotLabel || signatureModal.fileName || 'Documento').slice(0, 80);
            const via   = method === 'CLICKSIGN' ? 'Clicksign' : 'Gov.br';
            createOfficeNotification('Assinatura Solicitada', `O documento "${label}" requer assinatura via ${via}.`, 'SYSTEM', null);
            createClientSignatureNotification(workspaceKey, label, method);
            setSignatureModal(null);
          }}
          onClose={() => setSignatureModal(null)}
        />
      )}
      <OrganizadorProvasModal
        fileId={reorderModal?.fileId || ''}
        fileName={reorderModal?.fileName || ''}
        isOpen={!!reorderModal}
        onClose={() => setReorderModal(null)}
        onSuccess={() => { try { refreshDriveFiles(); } catch {} pushNotice('PDF reordenado com sucesso no Drive!'); }}
      />
      {petitionModal && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { if (!petitionBusy) setPetitionModal(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-emerald-400 p-6 w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-6 h-6 text-emerald-600" />
              <div>
                <h2 className="text-base font-black text-emerald-700 uppercase tracking-wider">Publicar Petição para o Cliente</h2>
                <p className="text-[10px] text-gray-500 mt-0.5">O documento será enviado ao acervo do cliente e exigirá consentimento informado.</p>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Título da Petição</label>
                <input
                  type="text"
                  value={petitionModal._title ?? String(petitionModal.slotLabel || petitionModal.fileName || '').trim()}
                  onChange={(e) => setPetitionModal((prev) => ({ ...prev, _title: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  placeholder="Ex: Petição Inicial — Ação de Indenização"
                />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="font-bold text-slate-500">Caderno:</span> <span className="text-slate-800">{petitionModal.cadernoTitle || '—'}</span></div>
                <div><span className="font-bold text-slate-500">Cliente:</span> <span className="text-slate-800">{toTitleCasePt(petitionModal.clientName) || petitionModal.clientId || '—'}</span></div>
                {petitionModal.parteContraria ? <div className="col-span-2"><span className="font-bold text-slate-500">Parte Contrária:</span> <span className="text-slate-800">{toTitleCasePt(petitionModal.parteContraria)}</span></div> : null}
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Arquivo da Petição (PDF / Word)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setPetitionModal((prev) => ({ ...prev, _file: f })); }}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-xs text-slate-700 file:mr-2 file:rounded-md file:border-0 file:bg-emerald-50 file:px-3 file:py-1 file:text-xs file:font-bold file:text-emerald-700"
                />
                <p className="text-[10px] text-slate-400 mt-1">Selecione o arquivo da petição para upload ao acervo do cliente.</p>
              </div>
              {!petitionModal.clientId && (
                <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                  ⚠️ Nenhum cliente vinculado a este Caderno de Autos. Vincule ao menos uma parte antes de publicar.
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setPetitionModal(null)} disabled={petitionBusy} className="flex-1 py-2 rounded-lg border border-gray-300 text-xs font-bold text-gray-600 hover:bg-gray-50 transition disabled:opacity-50">Cancelar</button>
              <button
                type="button"
                disabled={petitionBusy || !petitionModal.clientId || !petitionModal._file}
                onClick={() => {
                  const title = String(petitionModal._title ?? petitionModal.slotLabel ?? petitionModal.fileName ?? '').trim();
                  handlePublishPetition(petitionModal._file, {
                    title,
                    clientId: petitionModal.clientId,
                    cadernoId: petitionModal.cadernoId,
                    cadernoTitle: petitionModal.cadernoTitle,
                    slotLabel: petitionModal.slotLabel,
                    parteContraria: petitionModal.parteContraria,
                  });
                }}
                className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-extrabold uppercase tracking-widest disabled:opacity-50 transition"
              >
                {petitionBusy ? 'Publicando...' : '📤 Publicar Petição'}
              </button>
            </div>
          </div>
        </div>
      )}
      {aiWizardOpen && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { setAiWizardOpen(false); setAiWizardPhase('input'); }}>
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-purple-400 p-6 w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">🪄</span>
              <div>
                <h2 className="text-base font-black text-purple-700 uppercase tracking-wider">Importar Checklist da IA</h2>
                <p className="text-[10px] text-gray-500 mt-0.5">Cole o texto do NotebookLM / Gemini e o sistema extrai os documentos automaticamente</p>
              </div>
            </div>

            {aiWizardPhase === 'input' ? (
              <>
                <p className="text-xs text-gray-600 mb-2">Cole abaixo a lista gerada pela IA. Aceita numerada, com hífen, com dois-pontos, etc.</p>
                <textarea
                  autoFocus
                  value={aiWizardText}
                  onChange={(e) => setAiWizardText(e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none placeholder-gray-300"
                  placeholder={"1. Boleto de Abril (Vencimento dia 10): O boleto atual...\n2. Comprovantes de pagamento: Boletos anteriores...\n3. Contrato de Prestação de Serviços"}
                />
                <div className="flex gap-2 mt-4">
                  <button type="button" onClick={() => setAiWizardOpen(false)} className="flex-1 py-2 rounded-lg border border-gray-300 text-xs font-bold text-gray-600 hover:bg-gray-50 transition">Cancelar</button>
                  <button type="button" onClick={handleAIAnalyze} disabled={!aiWizardText.trim()} className="flex-1 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-extrabold uppercase tracking-widest disabled:opacity-50 transition">🔍 Analisar Texto</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-600 mb-3">
                  <strong className="text-purple-700">{aiWizardItems.filter((it) => it.selected).length}</strong> documento(s) selecionado(s) de <strong>{aiWizardItems.length}</strong> encontrado(s). Desmarque os que não deseja criar.
                </p>
                <div className="max-h-56 overflow-y-auto space-y-1.5 border border-gray-200 rounded-lg p-2 bg-gray-50">
                  {aiWizardItems.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">⚠️ Nenhum item identificado. Tente colar um texto com lista numerada ou com hífen.</p>
                  ) : (
                    aiWizardItems.map((item) => (
                      <label key={item.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-purple-50 cursor-pointer transition">
                        <input
                          type="checkbox"
                          checked={item.selected}
                          onChange={() => handleAIToggleItem(item.id)}
                          className="w-3.5 h-3.5 accent-purple-600 shrink-0"
                        />
                        <span className="text-xs text-gray-800 leading-snug">{item.name}</span>
                      </label>
                    ))
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <button type="button" onClick={() => setAiWizardPhase('input')} className="flex-1 py-2 rounded-lg border border-gray-300 text-xs font-bold text-gray-600 hover:bg-gray-50 transition">← Reeditar</button>
                  <button
                    type="button"
                    onClick={handleAIInjectSlots}
                    disabled={!aiWizardItems.some((it) => it.selected)}
                    className="flex-1 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-extrabold uppercase tracking-widest disabled:opacity-50 transition"
                  >✨ Criar {aiWizardItems.filter((it) => it.selected).length} Slot(s)</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {nukeOpen && (() => {
        // MC60 (03/09/2026, incidente real: caderno inteiro apagado por
        // engano — [CLIENTE_A], recuperado via Firestore PITR).
        // O texto antigo dizia "apagará o caderno {pieceType}" — pieceType
        // é o TIPO DA PEÇA ATIVA (ex.: "Contestação (Cível)"), não o nome
        // do caso. Lido rápido, parece "vou apagar só este documento" —
        // exatamente o oposto do que a ação faz (apaga TODAS as peças,
        // TODOS os anexos, TUDO no Drive, e TUDO no Firestore deste caso
        // inteiro, sem desfazer). Confirmação por palavra genérica
        // ("DELETAR") trocada por digitar o NOME DO CLIENTE de verdade —
        // força reconhecer QUAL caso está sendo apagado, não só que uma
        // ação destrutiva genérica vai acontecer.
        const nomeCliente = String(activeWorkspace?.clientName || '').trim();
        const cnj = String(activeWorkspace?.numeroCNJ || '').trim();
        const confirmAlvo = nomeCliente || 'DELETAR';
        const totalPecas = Array.isArray(activeWorkspace?.pieces)
          ? activeWorkspace.pieces.length
          : 0;
        return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => { setNukeOpen(false); setNukeText(''); }}>
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-red-500 p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">☢️</span>
              <h2 className="text-base font-black text-red-700 uppercase tracking-wider">Excluir o CASO inteiro</h2>
            </div>
            <p className="text-sm text-gray-700 mb-2">
              Isso apaga <strong>TODO</strong> o caderno de{' '}
              <strong className="text-gray-900">{nomeCliente || 'este cliente'}</strong>
              {cnj ? <> (processo {cnj})</> : null} — todas as {totalPecas || ''} peça(s),
              todos os anexos vinculados, e tenta apagar as pastas correspondentes no
              Google Drive. <strong>Não é só a peça aberta agora — é o caso inteiro.</strong>
            </p>
            <p className="text-xs text-red-600 font-bold mt-3 mb-1">
              Sem desfazer depois. Digite{' '}
              <code className="bg-red-50 px-1 rounded">{confirmAlvo}</code> para confirmar:
            </p>
            <input
              autoFocus
              value={nukeText}
              onChange={(e) => setNukeText(e.target.value.toUpperCase())}
              className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder={confirmAlvo}
            />
            <div className="flex gap-2 mt-4">
              <button type="button" onClick={() => { setNukeOpen(false); setNukeText(''); }} className="flex-1 py-2 rounded-lg border border-gray-300 text-xs font-bold text-gray-600 hover:bg-gray-50 transition">Cancelar</button>
              <button type="button" onClick={handleNukeWorkspace} disabled={nukeText.trim().toUpperCase() !== confirmAlvo.toUpperCase() || nukeBusy} className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-extrabold uppercase tracking-widest disabled:opacity-50 transition">{nukeBusy ? 'Apagando...' : '☢️ Apagar o caso inteiro'}</button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}



